import { StyleSheet } from 'react-native-unistyles';

import { theme } from '@squire/ui';

const CARD_WIDTH = 520;
const BRANDING_COLUMN_WIDTH = 800;
const SINGLE_CONTENT_WIDTH = 520;
// SquireLogoText's viewBox is 47x15, and react-native-svg-web needs an explicit numeric height in
// `style` — the `height` prop alone renders as 0px.
export const SINGLE_LOGO_WIDTH = 80;
const SINGLE_LOGO_HEIGHT = (SINGLE_LOGO_WIDTH * 15) / 47;

export const styles = StyleSheet.create((colors, rt) => ({
  // Columns pin to the edges; `gap` is the minimum, so laptop widths are unchanged.
  desktopRoot: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    padding: theme.spacing[9],
    gap: theme.spacing[9],
  },
  // Capped so wide-screen room becomes the middle gap, not a wider column.
  brandingColumn: {
    flexGrow: 0,
    flexShrink: 1,
    flexBasis: BRANDING_COLUMN_WIDTH,
    maxWidth: BRANDING_COLUMN_WIDTH,
  },
  card: {
    width: CARD_WIDTH,
    maxWidth: '100%',
    borderWidth: 1,
    borderColor: colors.borderSecondaryAlpha,
    borderRadius: theme.borderRadius.l,
    backgroundColor: colors.backgroundL0,
    overflow: 'hidden',
    // Containing block for a card-scoped overlay (the leave drawer).
    position: 'relative',
  },
  cardHeader: {
    padding: theme.spacing[6],
    gap: theme.spacing[3],
  },
  cardContent: {
    flex: 1,
    paddingHorizontal: theme.spacing[6],
    // The header carries the top inset; without a matching bottom one the last line sits flush.
    paddingBottom: theme.spacing[6],
  },
  // Steps that render their own title lose the header's top padding; add it back.
  cardContentNoHeader: {
    paddingTop: theme.spacing[6],
  },
  cardFooter: {
    padding: theme.spacing[6],
    gap: theme.spacing[5],
  },
  // Fills the card so the drawer and its scrim are contained, and gives the dialog role a box.
  overlayHost: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
  mobileRoot: {
    flex: 1,
  },
  mobileContent: {
    flex: 1,
    paddingHorizontal: theme.spacing[5],
    paddingVertical: theme.spacing[6],
    gap: theme.spacing[7],
  },
  mobileStep: {
    flex: 1,
  },
  mobileFooter: {
    paddingHorizontal: theme.spacing[5],
    paddingBottom: theme.spacing[6],
    gap: theme.spacing[5],
  },
  // Single centered column (payment flow): full-page background, logo over a
  // capped-width content column, both web and mobile — mobile's narrower
  // viewport caps the column at its own width via `maxWidth`. Horizontal
  // inset lives ONLY on `singleContent`/`singleFooter` below (not here too) —
  // stacking it on both boxes double-applied the 16px gutter, over-shrinking
  // the mobile column to 329px against the 361px the design calls for and
  // clipping the countdown pill / tip value at the right edge (Guido QA).
  // The centering/vertical-padding/gap that used to live here moved onto
  // Only fills the viewport so the background and the overlay's absolute fill cover it; the scroll
  // itself lives on `singleScroll`.
  singleRoot: {
    flex: 1,
    backgroundColor: colors.backgroundPrimary,
  },
  singleScroll: {
    flex: 1,
    width: '100%',
  },
  singleScrollContent: {
    flexGrow: 1,
    minHeight: '100%',
    alignItems: 'center',
    paddingTop: theme.spacing[12],
    // Flat 64px top and bottom, plus the safe-area inset on native so the CTA clears the home
    // indicator. `rt.insets.bottom` is 0 on web, so the web value is unchanged.
    paddingBottom: rt.insets.bottom + theme.spacing[12],
    gap: theme.spacing[9],
  },
  singleHeader: {
    width: '100%',
    maxWidth: SINGLE_CONTENT_WIDTH,
    alignSelf: 'center',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: rt.insets.top + theme.spacing[6],
    paddingHorizontal: theme.spacing[5],
  },
  // Under the pinned header the scroll starts at its 40px gap, not the page's
  // 64px top padding.
  singleScrollContentUnderHeader: {
    paddingTop: theme.spacing[9],
  },
  singleLogo: {
    width: SINGLE_LOGO_WIDTH,
    height: SINGLE_LOGO_HEIGHT,
    flexShrink: 0,
  },
  singleContent: {
    width: '100%',
    maxWidth: SINGLE_CONTENT_WIDTH,
    paddingHorizontal: theme.spacing[5],
  },
  singleFooter: {
    width: '100%',
    maxWidth: SINGLE_CONTENT_WIDTH,
    paddingHorizontal: theme.spacing[5],
  },
  // Fills `singleRoot` so an overlay covers the whole viewport, not just the capped content column.
  singleOverlayHost: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },
}));
